import { AxiosError } from 'axios';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import LoadingSpinner from '../components/LoadingSpinner';
import MobileModal from '../components/MobileModal';
import { PermissionButton } from '../components/PermissionButton';
import { Alert, ConfirmDialog, EmptyState, ExportButton, FormActions, FormDateInput, FormField, FormTextarea, PageHeader, TableActions } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useErrorHandler } from '../utils/errorHandler';
import { ExcelColumn } from '../utils/exportToExcel';

interface Vineyard {
  id: number;
  name: string;
  location: string;
  planted_variety: string;
  planting_date: string;
  acreage?: number;
  brix?: number;
  ph?: number;
  ta?: number;
  sg?: number;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface VineyardFormValues {
  name: string;
  location: string;
  planted_variety: string;
  planting_date: string;
  acreage: number;
  notes?: string;
}

const VineyardSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  location: Yup.string().required('Location is required'),
  planted_variety: Yup.string().required('Variety is required'),
  planting_date: Yup.string().required('Planting date is required'),
  acreage: Yup.number()
    .required('Size is required')
    .positive('Size must be positive'),
  notes: Yup.string()
});

// Helper function for safer date formatting
const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString) return 'N/A';
  try {
    // First, try to clean the date string if needed
    let cleanDateStr = dateString;

    // Handle timezone issues by ensuring we're working with a clean YYYY-MM-DD format
    if (dateString.includes('T')) {
      cleanDateStr = dateString.split('T')[0];
    }

    // Try to create a valid date object
    const date = new Date(cleanDateStr);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error("Invalid date detected:", dateString);
      return 'Invalid Date';
    }

    // Use a more standard, unambiguous format
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    console.error("Error formatting date:", dateString, error);
    return 'Invalid Date';
  }
};

const Vineyards: React.FC<{ tabVisible?: boolean }> = ({ tabVisible = true }) => {
  const [vineyards, setVineyards] = useState<Vineyard[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingVineyard, setEditingVineyard] = useState<Vineyard | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; vineyardId?: number }>({ open: false });
  const { user } = useAuth();
  const { handleError } = useErrorHandler();

  const isAdmin = (user?.Role?.name || (user as any)?.role?.name || '').toLowerCase() === 'admin';

  // Export columns definition for ExportButton
  const exportColumns: ExcelColumn<Vineyard>[] = [
    { header: 'Name', accessor: (v) => v.name },
    { header: 'Location', accessor: (v) => v.location },
    { header: 'Variety', accessor: (v) => v.planted_variety || '' },
    { header: 'Planting Date', accessor: (v) => v.planting_date || '' },
    {
      header: 'Size (Acres)',
      accessor: (v) =>
        typeof v.acreage === 'number' ? v.acreage.toFixed(2) : '',
    },
    {
      header: 'Created By',
      accessor: (v) => {
        const creator = (v as any).creator;
        if (!creator) return 'Unknown';
        return `${creator.first_name ?? ''} ${creator.last_name ?? ''}`.trim();
      },
    },
  ];

  const handleDelete = async (vineyardId: number) => {
    if (!isAdmin) return;
    setConfirmDelete({ open: true, vineyardId });
  };

  const confirmDeleteVineyard = async () => {
    if (!confirmDelete.vineyardId) return;
    try {
      await api.delete(`/api/vineyard/${confirmDelete.vineyardId}`);
      setVineyards(vineyards.filter(v => v.id !== confirmDelete.vineyardId));
      setSuccessMessage('Vineyard deleted successfully');
      setConfirmDelete({ open: false });
    } catch (err: any) {
      const formattedError = handleError(err as AxiosError, 'Delete Vineyard');
      setError(formattedError.message);
      setConfirmDelete({ open: false });
    }
  };

  // Fetch vineyards when tab is visible (refetch when switching to this tab)
  useEffect(() => {
    if (!tabVisible) return;
    const fetchVineyards = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/vineyard');
        console.log("Raw API Response:", response.data);
        const parsedVineyards = response.data.map((v: any) => ({
          ...v,
          // Check for both camelCase and snake_case properties
          planted_variety: v.plantedVariety || v.planted_variety || '',
          planting_date: v.plantingDate || v.planting_date || '',
          acreage: v.acreage ? parseFloat(v.acreage) : undefined,
          brix: v.brix ? parseFloat(v.brix) : undefined,
          ph: v.ph ? parseFloat(v.ph) : undefined,
          ta: v.ta ? parseFloat(v.ta) : undefined,
          sg: v.sg ? parseFloat(v.sg) : undefined,
        }));
        console.log("Parsed Vineyards (set in state):", parsedVineyards);
        setVineyards(parsedVineyards);
      } catch (err: any) {
        const formattedError = handleError(err as AxiosError, 'Fetch Vineyards');
        setError(formattedError.message);
      } finally {
        setLoading(false);
      }
    };

    fetchVineyards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabVisible]);

  const openModal = (vineyard: Vineyard | null = null) => {
    setEditingVineyard(vineyard);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingVineyard(null);
  };

  const handleSubmit = async (values: VineyardFormValues, { resetForm, setSubmitting }: any) => {
    try {
      // Clear previous errors
      setError(null);

      console.log('Raw form values:', values);

      // Ensure all required fields are present and properly formatted with correct snake_case names
      const formattedValues = {
        name: values.name?.trim() || '',
        location: values.location?.trim() || '',
        planted_variety: values.planted_variety?.trim() || '',
        planting_date: values.planting_date || '',
        acreage: values.acreage || 0,
        notes: values.notes?.trim() || ''
      };

      console.log('Formatted values:', formattedValues);

      // Client-side validation
      if (!formattedValues.planted_variety) {
        setError('Planted variety is required');
        setSubmitting(false);
        return;
      }

      if (!formattedValues.planting_date) {
        setError('Valid planting date is required');
        setSubmitting(false);
        return;
      }

      // Convert to camelCase for API submission (this is what the server expects)
      const apiSubmissionData = {
        name: formattedValues.name,
        location: formattedValues.location,
        plantedVariety: formattedValues.planted_variety, // Convert to camelCase
        plantingDate: formattedValues.planting_date,     // Convert to camelCase
        acreage: formattedValues.acreage,
        notes: formattedValues.notes
      };

      console.log('API submission data (camelCase):', apiSubmissionData);

      if (editingVineyard) {
        // Update existing vineyard
        const response = await api.put(`/api/vineyard/${editingVineyard.id}`, apiSubmissionData);
        console.log('Update response:', response.data);

        // Transform the response data to ensure snake_case fields are populated
        const updatedVineyard = {
          ...response.data,
          planted_variety: response.data.plantedVariety || response.data.planted_variety,
          planting_date: response.data.plantingDate || response.data.planting_date,
          // Ensure numeric values are parsed
          acreage: response.data.acreage ? parseFloat(response.data.acreage) : undefined,
          brix: response.data.brix ? parseFloat(response.data.brix) : undefined,
          ph: response.data.ph ? parseFloat(response.data.ph) : undefined,
          ta: response.data.ta ? parseFloat(response.data.ta) : undefined,
          sg: response.data.sg ? parseFloat(response.data.sg) : undefined,
        };

        setVineyards(vineyards.map(v => (v.id === editingVineyard.id ? updatedVineyard : v)));
        setSuccessMessage('Vineyard updated successfully!');
      } else {
        // Create new vineyard
        console.log('Creating new vineyard with data:', apiSubmissionData);
        const response = await api.post('/api/vineyard', apiSubmissionData);
        console.log('Creation response:', response.data);

        // Transform the response data to ensure snake_case fields are populated
        const newVineyard = {
          ...response.data,
          planted_variety: response.data.plantedVariety || response.data.planted_variety,
          planting_date: response.data.plantingDate || response.data.planting_date,
          // Ensure numeric values are parsed
          acreage: response.data.acreage ? parseFloat(response.data.acreage) : undefined,
          brix: response.data.brix ? parseFloat(response.data.brix) : undefined,
          ph: response.data.ph ? parseFloat(response.data.ph) : undefined,
          ta: response.data.ta ? parseFloat(response.data.ta) : undefined,
          sg: response.data.sg ? parseFloat(response.data.sg) : undefined,
        };

        setVineyards([...vineyards, newVineyard]);
        setSuccessMessage('Vineyard created successfully!');
      }
      resetForm();
      closeModal();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      const formattedError = handleError(err as AxiosError, 'Save Vineyard');
      setError(formattedError.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Vineyard Blocks"
        subtitle="Manage vineyard blocks and activities."
        variant="compact"
        actions={(
          <div className="flex gap-3">
            <ExportButton
              data={vineyards}
              columns={exportColumns}
              fileName="vineyards"
              sheetName="Vineyards"
            />
            <PermissionButton
              resource="vineyard"
              action="create"
              onClick={() => openModal()}
            >
              Add Vineyard
            </PermissionButton>
          </div>
        )}
      />

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => setError(null)}
          className="mt-4"
        />
      )}

      {successMessage && (
        <Alert
          type="success"
          message={successMessage}
          autoHide
          onClose={() => setSuccessMessage(null)}
          className="mt-4"
        />
      )}

      <div className="mt-8 flex flex-col">
        {/* Desktop Table View */}
        <div className="hidden md:block -my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow border border-monday-border-light md:rounded-lg">
              <table className="min-w-full divide-y divide-monday-border-light">
                <thead className="bg-monday-header">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-monday-text-muted sm:pl-6">Name</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text-muted">Location</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text-muted">Variety</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text-muted">Planting Date</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text-muted">Size</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text-muted">Created By</th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-monday-border-light bg-white">
                  {vineyards.length > 0 ? (
                    vineyards.map((vineyard) => (
                      <tr key={vineyard.id} className="group hover:bg-monday-hover transition-colors duration-150">
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-monday-text sm:pl-6">{vineyard.name}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">{vineyard.location}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">{vineyard.planted_variety || 'N/A'}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">{formatDate(vineyard.planting_date)}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">{vineyard.acreage?.toFixed(2) ?? 'N/A'}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                          {(vineyard as any).creator ?
                            `${(vineyard as any).creator.first_name} ${(vineyard as any).creator.last_name}` :
                            'Unknown'
                          }
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <TableActions>
                            <PermissionButton
                              resource="vineyard"
                              action="update"
                              onClick={() => openModal(vineyard)}
                              variant="secondary"
                              className="!px-2.5 !py-1 !text-sm !bg-transparent !text-monday-text-muted hover:!bg-monday-hover hover:!text-monday-text !border-0 !shadow-none"
                            >
                              Edit
                            </PermissionButton>
                            {isAdmin && (
                              <button
                                onClick={() => handleDelete(vineyard.id)}
                                className="px-2.5 py-1 text-sm font-medium text-monday-text-muted hover:text-error-600 hover:bg-error-50 rounded-md transition-colors"
                                title="Delete vineyard"
                              >
                                Delete
                              </button>
                            )}
                          </TableActions>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7}>
                        <EmptyState
                          title="No vineyards found"
                          message='Click "Add Vineyard" to create your first vineyard block.'
                          action={{
                            label: 'Add Vineyard',
                            onClick: () => openModal(),
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* Mobile Card/List View */}
        <div className="md:hidden space-y-4">
          {vineyards.length > 0 ? (
            vineyards.map((vineyard) => (
              <div key={vineyard.id} className="bg-white/95 rounded-xl shadow border border-monday-border-light p-4 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-lg font-bold text-monday-text">{vineyard.name}</div>
                    <div className="text-sm text-monday-text-muted">{vineyard.location}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <PermissionButton
                      resource="vineyard"
                      action="update"
                      onClick={() => openModal(vineyard)}
                      variant="secondary"
                      className="!ml-2 !px-3 !py-1 !text-sm"
                    >
                      Edit
                    </PermissionButton>
                    {isAdmin && (
                      <button
                        onClick={() => handleDelete(vineyard.id)}
                        className="px-3 py-1 rounded-lg bg-red-50 text-red-700 text-sm font-medium hover:bg-red-100 border border-red-200"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 mt-2">
                  <div>
                    <span className="block text-xs text-monday-text-muted">Variety</span>
                    <span className="text-sm text-monday-text">{vineyard.planted_variety || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-monday-text-muted">Planting Date</span>
                    <span className="text-sm text-monday-text">{formatDate(vineyard.planting_date)}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-monday-text-muted">Size</span>
                    <span className="text-sm text-monday-text">{vineyard.acreage?.toFixed(2) ?? 'N/A'}</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <EmptyState
              title="No vineyards found"
              message='Click "Add Vineyard" to create your first vineyard block.'
              action={{
                label: 'Add Vineyard',
                onClick: () => openModal(),
              }}
            />
          )}
        </div>
      </div>

      <MobileModal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingVineyard ? 'Edit Vineyard' : 'Add New Vineyard'}
        size="full"
      >
        <div className="mobile-form-container">
          <Formik
            initialValues={editingVineyard ? {
              name: editingVineyard.name ?? '',
              location: editingVineyard.location ?? '',
              planted_variety: editingVineyard.planted_variety ?? '',
              planting_date: editingVineyard.planting_date ?? '',
              acreage: editingVineyard.acreage ?? 0,
              notes: editingVineyard.notes ?? ''
            } : {
              name: '',
              location: '',
              planted_variety: '',
              planting_date: '',
              acreage: 0,
              notes: ''
            }}
            validationSchema={VineyardSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form className="space-y-4">
                <FormField name="name" label="Vineyard Name" type="text" required />
                <FormField name="location" label="Location" type="text" required />
                <FormField name="planted_variety" label="Planted Variety" type="text" required />
                <FormDateInput name="planting_date" label="Planting Date" required />
                <FormField name="acreage" label="Size" type="number" step="0.01" min={0} />
                <FormTextarea name="notes" label="Notes" rows={3} />
                <FormActions onCancel={closeModal} isSubmitting={isSubmitting} submitText="Save" />
              </Form>
            )}
          </Formik>
        </div>
      </MobileModal>

      <ConfirmDialog
        isOpen={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false })}
        onConfirm={confirmDeleteVineyard}
        title="Delete Vineyard"
        message="Are you sure you want to delete this vineyard? This action cannot be undone."
        variant="danger"
      />
    </div>
  );
};

export default Vineyards;
